import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_HREF } from '@nrx/booking-ui';
import { Alert, Box, Button, Card, Container, Heading, Link, Text } from '@nrx/ui';
import { Component, type ReactNode } from 'react';

import { reportConsultError } from '../../lib/report-consult-error.js';

type ConsultErrorBoundaryProps = { children: ReactNode };
type ConsultErrorBoundaryState = { failed: boolean };

/**
 * Catches any render-time throw below it and degrades to a visible, actionable surface.
 *
 * @remarks THE REAL BOUNDARY. The PR body claimed one existed; it did not — `buildBookingUrl`'s
 * null-returning parse is a totality fix at ONE throw site, which closes one instance of the bug
 * and not its class. Review finding 1 was the live proof: a different render-time throw, from
 * ordinary API response data, blanked the entire SPA exactly as the original bug did. That throw is
 * now fixed at source AND contained here, because the next one will be somewhere nobody predicted.
 *
 * Placed INSIDE `ConsultLayout` (see `app.tsx`) rather than around it, so the brand header and the
 * footer with the legal links survive a failure and the member is never looking at a bare white
 * page. A boundary wrapping the layout would take the chrome down with the content.
 *
 * `Alert tone="danger"` carries `role="alert"`, so the failure is announced rather than only drawn
 * (W4) — the member this exists for may be reading with a screen reader, and the previous behaviour
 * announced nothing at all because there was nothing to announce.
 *
 * Reload rather than "retry": the boundary cannot know what the failed subtree needed, and React
 * gives no way to re-run it cleanly from here. A full reload is honest and, for the data-shape
 * class of failure this catches, is also what a support engineer would ask for first.
 */
export class ConsultErrorBoundary extends Component<
  ConsultErrorBoundaryProps,
  ConsultErrorBoundaryState
> {
  override state: ConsultErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ConsultErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error): void {
    // The whole point of the boundary is that the member no longer sees the crash — so if this is
    // not reported, the failure becomes invisible to everyone instead of only to the operator.
    reportConsultError('render', error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <Container as="section" size="4" pt="8" pb="10">
        <Card elevation="soft" className="consult-panel consult-panel--narrow">
          <Heading as="h1" size="7" className="consult-panel__title">
            Something went wrong
          </Heading>
          <Alert tone="danger">
            <Text as="p">
              We couldn&apos;t load your scheduling page. Reloading usually fixes it.
            </Text>
          </Alert>
          <Box className="consult-panel__contact">
            <Button
              tone="primary"
              onClick={() => {
                window.location.reload();
              }}
              className="consult-inline-action"
            >
              Reload the page
            </Button>
          </Box>
          <Text as="p" color="muted" className="consult-panel__contact">
            Still stuck? Call us at{' '}
            <Link href={SUPPORT_PHONE_HREF} underline>
              <Text weight="medium">{SUPPORT_PHONE_DISPLAY}</Text>
            </Link>{' '}
            or email{' '}
            <Link href="mailto:food@nourishedrx.com" underline>
              <Text weight="medium">food@nourishedrx.com</Text>
            </Link>
            .
          </Text>
        </Card>
      </Container>
    );
  }
}
